import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'

import NavigationArrow from 'components/NavigationArrow/NavigationArrow'

import { MAX_BUILDINGS_COUNT } from '../../constants'
import { TIME_CONSTANT, CAROUSEL_MARGINS } from './constants'

import styles from './KineticScrolling.scss'

class KineticScrolling extends React.Component {

    constructor(props) {
        super(props)

        this.state = {
            showArrows: false,
            visibleItems: this.countVisibleItems(),
            pressed: false,
            reference: null,
            offset: 0,
            min: 0,
            max: 0,
            dragging: false,
        }

        this.prev = this.prev.bind(this)
        this.next = this.next.bind(this)

        this.handleTap = this.handleTap.bind(this)
        this.handleDrag = this.handleDrag.bind(this)
        this.handleRelease = this.handleRelease.bind(this)
        this.handleClick = this.handleClick.bind(this)
        this.handleWheel = this.handleWheel.bind(this)
        this.handleResize = this.handleResize.bind(this)

        this.countVisibleItems = this.countVisibleItems.bind(this)
    }

    componentDidMount() {
        this.update()
        if (this.props.activeItemIdx >= this.state.visibleItems) {
            setTimeout(() => this.scrollToItem(this.props.activeItemIdx), 300)
        }
        window.addEventListener('resize', this.handleResize)
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize)
    }

    componentWillReceiveProps(newProps) {
        if (newProps.activeItemIdx !== this.props.activeItemIdx) {
            const newOffset = newProps.activeItemIdx * this.props.snap
            const maxVisibleOffsetDifference = this.state.visibleItems * this.props.snap
            const scrollToLeft = (newOffset - this.state.offset) <= 0
            const scrollToRight = (newOffset - this.state.offset) >= maxVisibleOffsetDifference

            if (scrollToLeft || scrollToRight) {
                this.scrollToItem(newProps.activeItemIdx)
            }
        }
    }

    setMaxTransform() {
        const max = this._view.offsetWidth - this._holder.offsetWidth
        this.setState({ max })
    }

    update() {
        this.setMaxTransform()
        this.setState({
            visibleItems: this.countVisibleItems(),
            showArrows: this._view.offsetWidth > this._holder.offsetWidth,
        })

        const boundAutoscroll = this.autoScroll.bind(this)
        requestAnimationFrame(boundAutoscroll)
    }

    pos(e) {
        // touch event
        const clientPos = 'clientX'
        if (e.targetTouches && (e.targetTouches.length >= 1)) {
            return e.targetTouches[0][clientPos]
        }

        // mouse event
        return e[clientPos]
    }

    scroll(x) {
        const { min, max } = this.state
        let offset = (x > max) ? max : null
        if (!offset) {
            offset = (x < min) ? min : x
        }

        this.setState({ offset })
        this.trackState()
    }

    track() {
        const now = Date.now()
        const elapsed = now - this.state.timestamp
        this.setState({
            timestamp: now,
        })
        const delta = this.state.offset - this.state.frame
        const v = 1000 * delta / (1 + elapsed)

        this.setState({
            frame: this.state.offset,
            velocity: 0.8 * v + 0.2 * this.state.velocity,
        })
    }

    autoScroll() {
        let elapsed
        let delta

        if (this.state.amplitude) {
            elapsed = Date.now() - this.state.timestamp
            delta = -this.state.amplitude * Math.exp(-elapsed / TIME_CONSTANT)
            if (delta > 5 || delta < -5) {
                this.scroll(this.state.target + delta)
                const boundAutoscroll = this.autoScroll.bind(this)
                requestAnimationFrame(boundAutoscroll)
            } else {
                this.scroll(this.state.target)
            }
        }
    }

    // Public methods

    scrollToItem(idx) {
        if (this.props.snap) {
            const target = idx * this.props.snap
            const amplitude = target - this.state.offset

            this.setState({
                target,
                amplitude,
                timestamp: Date.now(),
                enableTransitions: true,
            })

            this.scroll(target)
        }
    }

    prev() {
        if (this.props.snap) {
            this.scrollToItem(this.current() - 1)
        }
    }

    next() {
        if (this.props.snap) {
            this.scrollToItem(this.current() + 1)
        }
    }

    current() {
        let current
        if (this.state.offset === 0) {
            current = 0
        } else {
            current = Math.round(this.state.offset / this.props.snap)
        }
        return current
    }

    atBegin() {
        return this.state.offset === 0
    }

    atEnd() {
        return this.state.offset === this.state.max
    }


    // Events handlers

    handleTap(e) {
        this.setState({
            pressed: true,
            reference: this.pos(e),
            velocity: 0,
            amplitude: 0,
            timestamp: Date.now(),
            frame: this.state.offset,
        })

        clearInterval(this.ticker)
        const boundTrack = this.track.bind(this)
        this.ticker = setInterval(boundTrack, 100)

        this.trackState()

        window.addEventListener('mousemove', this.handleDrag)
        window.addEventListener('mouseup', this.handleRelease)

        e.preventDefault()
        e.stopPropagation()
        return false
    }

    handleDrag(e) {
        let x
        let delta

        if (this.state.pressed) {
            x = this.pos(e)
            delta = this.state.reference - x

            if (delta > 2 || delta < -2) {
                this.setState({
                    reference: x,
                    dragging: true,
                    enableTransitions: false,
                })

                this.scroll(this.state.offset + delta)
            }
        }

        e.preventDefault()
        e.stopPropagation()
        return false
    }

    handleRelease(e) {
        window.removeEventListener('mousemove', this.handleDrag)
        window.removeEventListener('mouseup', this.handleRelease)

        this.setState({
            pressed: false,
        })

        clearInterval(this.ticker)

        if (!this.state.dragging) return

        let amplitude
        let target = this.state.offset

        if (this.state.velocity > 10 || this.state.velocity < -10) {
            amplitude = 0.2 * this.state.velocity
            target = Math.round(this.state.offset + amplitude)
        }

        target = Math.round(target / this.props.snap) * this.props.snap
        amplitude = target - this.state.offset

        this.setState({
            target,
            amplitude,
            timestamp: Date.now(),
        })

        const boundAutoscroll = this.autoScroll.bind(this)
        requestAnimationFrame(boundAutoscroll)
        if (!e.currentTarget.getAttribute || (e.currentTarget.getAttribute('id') !== this._view.id)) {
            this.setState({
                dragging: false,
            })
        }
    }

    handleWheel(e) {
        let target = this.state.offset + e.deltaY

        if (this.props.snap) {
            if (e.deltaY > 0) {
                target = (this.current() + 1) * this.props.snap
            } else {
                target = (this.current() - 1) * this.props.snap
            }
        }
        const amplitude = target - this.state.offset

        this.setState({
            target,
            amplitude,
            timestamp: Date.now(),
            enableTransitions: false,
        })

        const boundAutoscroll = this.autoScroll.bind(this)
        requestAnimationFrame(boundAutoscroll)
    }

    handleClick() {
        this.setState({
            dragging: false,
        })
    }

    handleResize() {
        this.update()
        this.forceUpdate()
    }

    countVisibleItems() {
        let visibleItems = 1
        for (let n = 1; n < MAX_BUILDINGS_COUNT; n++) {
            const maxWidth = CAROUSEL_MARGINS + this.props.snap * n

            if (!window.matchMedia(`(max-width: ${maxWidth}px)`).matches) {
                visibleItems = n
            }
        }
        return visibleItems
    }

    // Callback returning KineticCarousel state

    trackState() {
        if (this.props.trackState) {
            this.props.trackState({
                dragging: this.state.dragging,
            })
        }
    }

    render() {
        const arrowLeftClass = classNames(styles.arrow, styles.arrow__left)
        const arrowRightClass = classNames(styles.arrow, styles.arrow__right)

        const leftArrowDisabled = this.atBegin()
        const rightArrowDisabled = this.atEnd()

        let arrows
        if (this.state.showArrows) {
            arrows = (
                <div>
                    <div className={arrowLeftClass}>
                        <NavigationArrow
                            type='left'
                            handleClick={this.prev}
                            disabled={leftArrowDisabled}
                        />
                    </div>
                    <div className={arrowRightClass}>
                        <NavigationArrow
                            type='right'
                            handleClick={this.next}
                            disabled={rightArrowDisabled}
                        />
                    </div>
                </div>
            )
        }

        const holderClass = classNames(styles.holder, {
            [styles.holder__hideLeft]: leftArrowDisabled,
            [styles.holder__hideRight]: rightArrowDisabled,
        })

        const viewStyle = {
            transform: this.state.showArrows ? `translateX(${-this.state.offset}px)` : null,
        }

        const viewClass = classNames(styles.view, {
            [styles.view__enableTransitions]: this.state.enableTransitions,
        })

        const className = classNames(styles.base, {
            [styles.base__disabled]: !this.state.showArrows,
        })

        return (
            <div className={className}>
                <div className={holderClass} ref={_ => this._holder = _}>
                    <div
                        ref={_ => this._view = _}
                        style={viewStyle}
                        className={viewClass}
                        onMouseDown={this.handleTap}
                        onMouseUp={this.handleRelease}
                        onClick={this.handleClick}
                        onWheel={this.handleWheel}
                    >
                        {this.props.children}
                    </div>
                </div>
                {arrows}
            </div>
        )
    }
}

KineticScrolling.propTypes = {
    children: PropTypes.arrayOf(PropTypes.node).isRequired,
    snap: PropTypes.number,
    activeItemIdx: PropTypes.number,
    trackState: PropTypes.func,
}

export default KineticScrolling



// WEBPACK FOOTER //
// ./containers/BuildingsCarousel/components/KineticScrolling/KineticScrolling.jsx